import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { RiskySuppliersResponse } from '@marstein-radar/shared';
import { tableStyle, thStyle, tdStyle, buttonStyle, formatEur } from '../styles';

const INSOLVENCY_TYPE_LABEL: Record<string, string> = {
  konkurz_restrukturalizacia: 'Konkurz/reštrukturalizácia',
  likvidacia: 'Likvidácia',
};

// "Rizikoví dodávatelia štátu" (Týž. 21, 2026-07-18) — analýza vzišlá z
// prieskumu celého Slovensko.Digital ekosystému + práve dokončeného CRZ
// backfillu. Firmy, ktoré podpísali zmluvu so štátom AŽ PO tom, čo už boli
// v konkurze/reštrukturalizácii/likvidácii — nie len "niekedy v histórii
// mali insolvenčnú udalosť" (viď crz.repository.ts komentár).
export function RiskySuppliersPage() {
  const [page, setPage] = useState(1);
  // Týž. 21 (2026-07-19) — po hromadnom prepočte risk_score pre 637 798
  // firiem (predtým len 23, viď bulk-risk-score.ts) dáva zmysel filtrovať
  // aj podľa aktuálneho celkového rizikového skóre, nie len podľa
  // insolvenčného časovania.
  const [minRiskScore, setMinRiskScore] = useState('');

  const { data, isFetching } = useQuery<RiskySuppliersResponse>({
    queryKey: ['risky-suppliers', page, minRiskScore],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page) });
      if (minRiskScore.trim()) params.set('minRiskScore', minRiskScore.trim());
      const res = await fetch(`/api/v1/crz/risky-suppliers?${params.toString()}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <main className="page">
      <div className="page-header">
        <h1>Rizikoví dodávatelia štátu</h1>
        <p>
          Firmy, ktoré podpísali zmluvu so štátom až po tom, čo už boli v konkurze alebo likvidácii.
        </p>
      </div>

      <div className="fin-panel" style={{ padding: '14px 20px', marginBottom: '1.5rem' }}>
        Zdroj: Centrálny register zmlúv × insolvenčné udalosti (Obchodný vestník). Zoznam zámerne
        neobsahuje firmy, ktoré mali insolvenčnú udalosť niekedy v minulosti a zmluvu podpísali{' '}
        <strong>pred</strong> ňou — len prípady, kde dátum podpisu zmluvy je{' '}
        <strong>rovnaký alebo neskorší</strong> ako dátum prvej konkurznej/likvidačnej udalosti.
        Zmluvy nad 100 mil. € vylúčené ako odľahlé hodnoty. Údaje sú informatívne, nie právne
        záväzné — nezohľadňujú, či bola zmluva napokon reálne plnená.
      </div>

      <div className="search-filter-panel">
        <div className="filter-grid">
          <label className="filter-field filter-field-narrow">
            <span>Minimálne rizikové skóre firmy</span>
            <input
              type="number"
              min={0}
              max={100}
              value={minRiskScore}
              onChange={(e) => {
                setPage(1);
                setMinRiskScore(e.target.value);
              }}
              placeholder="0–100"
            />
          </label>
        </div>
      </div>

      {isFetching && !data && (
        <div className="fin-panel">
          {Array.from({ length: 8 }).map((_, i) => (
            <div className="skel-row" key={i}>
              <div>
                <div className="skel line1" />
                <div className="skel line2" />
              </div>
              <div className="skel pill" />
              <div className="skel num" />
            </div>
          ))}
        </div>
      )}
      {data && data.results.length > 0 && (
        <>
          <div className="section-title">
            <h2>Výsledky</h2>
            <span className="hint">{data.total.toLocaleString('sk-SK')} firiem spolu</span>
          </div>
          <div className="overflow-x">
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Firma</th>
                  <th style={thStyle} className="amt">
                    Rizikové skóre
                  </th>
                  <th style={thStyle}>Typ insolvencie</th>
                  <th style={thStyle} className="amt">
                    Prvá udalosť
                  </th>
                  <th style={thStyle} className="amt">
                    Posledná zmluva
                  </th>
                  <th style={thStyle} className="amt">
                    Počet zmlúv (po udalosti)
                  </th>
                  <th style={thStyle} className="amt">
                    Suma spolu
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((r) => (
                  <tr key={r.ico}>
                    <td style={tdStyle}>
                      <Link to={`/firma/${r.ico}`}>{r.name ?? r.ico}</Link>
                    </td>
                    <td style={tdStyle} className="amt num">
                      {r.riskScore ?? '—'}
                    </td>
                    <td style={tdStyle}>
                      {r.insolvencyTypes.map((t) => (
                        <span key={t} className="pill high" style={{ marginRight: 4 }}>
                          {INSOLVENCY_TYPE_LABEL[t] ?? t}
                        </span>
                      ))}
                    </td>
                    <td style={tdStyle} className="amt num">
                      {r.firstInsolvencyDate.slice(0, 10)}
                    </td>
                    <td style={tdStyle} className="amt num">
                      {r.latestSignedOn.slice(0, 10)}
                    </td>
                    <td style={tdStyle} className="amt num">
                      {r.contractCount}
                    </td>
                    <td style={tdStyle} className="amt num">
                      {formatEur(r.totalAmount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div
              style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginTop: '0.9rem' }}
            >
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{ ...buttonStyle, opacity: page <= 1 ? 0.5 : 1 }}
              >
                ← Predošlá
              </button>
              <span style={{ color: 'var(--slate)', fontSize: '0.85rem' }}>
                Strana {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{ ...buttonStyle, opacity: page >= totalPages ? 0.5 : 1 }}
              >
                Ďalšia →
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
}
